import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  INTERNAL_SE_SOFT_BLUE_COLORS,
  buildChartTheme,
  subtleHorizontalSplitLine,
  truncateChartLabel,
} from './chartTheme';
import useLaptopCompact from './useLaptopCompact';
import ChartEmptyState from './ChartEmptyState';

const BarChartHalf = ({
  rawData,
  embedded = false,
  renderZeroWhenEmpty = false,
  zeroNarratives = ['Security Incident Handling', 'Apex-Banking', 'Service Predictions', 'Apex-Online'],
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const isLaptopCompact = useLaptopCompact();
  const theme = buildChartTheme(isLaptopCompact);
  const [selectedRegion, setSelectedRegion] = useState('Total');
  const [chartData, setChartData] = useState({ xData: [], yData: [] });

  const hasRawData = !!(rawData && Object.keys(rawData).length > 0);
  const normalizedRawData = hasRawData
    ? rawData
    : {
        Total: zeroNarratives.map((narrative) => ({ Narrative: narrative, Count: 0 })),
      };

  useEffect(() => {
    if (normalizedRawData && normalizedRawData[selectedRegion]) {
      const regionData = normalizedRawData[selectedRegion];
      setChartData({
        xData: regionData.map((item) => item.Narrative),
        yData: regionData.map((item) => item.Count),
      });
    } else {
      setChartData({ xData: [], yData: [] });
    }
  }, [normalizedRawData, selectedRegion]);

  if (!hasRawData && !renderZeroWhenEmpty) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }
  const regions = Object.keys(normalizedRawData);

  const options = {
    animation: false,
    grid: { ...theme.gridWithLegend, bottom: isLaptopCompact ? 70 : 80 },
    tooltip: {
      ...theme.tooltipBase,
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params = []) => {
        const axis = params[0]?.axisValueLabel || params[0]?.axisValue || '';
        const value = params[0]?.value ?? 0;
        return `${axis}<br/>Narrative Runs: ${value}`;
      },
    },
    xAxis: {
      type: 'category',
      data: chartData.xData,
      axisLabel: {
        ...theme.axisLabel,
        rotate: 24,
        interval: 0,
        formatter: (value) => truncateChartLabel(value, 22),
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: chartData.yData.every((value) => Number(value) === 0) ? 1 : undefined,
      axisLabel: theme.axisLabel,
      splitLine: subtleHorizontalSplitLine,
    },
    dataZoom: chartData.xData.length > 6 ? [{
      type: 'slider',
      xAxisIndex: 0,
      start: 0,
      end: Math.min(100, Math.round((6 / chartData.xData.length) * 100)),
      bottom: isLaptopCompact ? 6 : 8,
      height: isLaptopCompact ? 16 : 18,
      showDetail: false,
      brushSelect: false,
    }] : [],
    series: [{
      data: chartData.yData.map((v, i) => ({
        value: v,
        itemStyle: { color: INTERNAL_SE_SOFT_BLUE_COLORS[i % INTERNAL_SE_SOFT_BLUE_COLORS.length] },
      })),
      type: 'bar',
      minPointSize: chartData.yData.every((value) => Number(value) === 0) ? 2 : 0,
      barMaxWidth: 40,
      label: { show: true, position: 'top', fontSize: theme.fs(11), color: '#374151' },
    }],
  };

  const header = (
    <div className={embedded ? 'lead-dash-card-header' : 'manager-console-chart-header'}>
      <h3 className="lead-dash-card-title">Top Narratives Globally</h3>
      <select
        className="lead-dash-select"
        value={selectedRegion}
        onChange={(e) => setSelectedRegion(e.target.value)}
        aria-label="Select region filter"
      >
        {regions.map((region) => (
          <option key={region} value={region}>{region}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div>
      {header}
      {chartData.xData.length === 0 && !renderZeroWhenEmpty ? (
        <ChartEmptyState
          title={emptyStateTitle}
          subtitle={emptyStateSubtitle}
          compact={emptyStateCompact}
        />
      ) : (
      <ReactECharts
        option={options}
        style={{ height: `${isLaptopCompact ? 320 : 340}px`, width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
      )}
    </div>
  );
};

export default BarChartHalf;
